import React, { useState } from 'react';
import { AlertCircleIcon, LoaderIcon, TicketIcon } from 'lucide-react';
import { HPEvent, Registration } from '../types';
import { PROFILE_OPTIONS } from '../data/events';
import { useRegistrations } from '../context/RegistrationsContext';
interface RegistrationFormProps {
  event: HPEvent;
  onSubmitted: (registration: Registration) => void;
}
interface FormState {
  fullName: string;
  email: string;
  organisation: string;
  jobTitle: string;
  profile: string;
  intent: string;
  dietary: string;
  consent: boolean;
}
const emptyForm: FormState = {
  fullName: '',
  email: '',
  organisation: '',
  jobTitle: '',
  profile: '',
  intent: '',
  dietary: '',
  consent: false
};
type Errors = Partial<Record<keyof FormState, string>>;
const fieldClass = 'mt-2 w-full rounded-lg border border-paper-line bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-muted/60 focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/20';
const labelClass = 'block text-sm font-medium text-ink';
export function RegistrationForm({
  event,
  onSubmitted
}: RegistrationFormProps) {
  const {
    submitRegistration
  } = useRegistrations();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  }
  function validate(): Errors {
    const next: Errors = {};
    if (!form.fullName.trim()) next.fullName = 'Please tell us your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = 'Enter a valid work email address.';
    if (!form.organisation.trim()) next.organisation = 'Your organisation helps us review the request.';
    if (!form.jobTitle.trim()) next.jobTitle = 'Please add your role.';
    if (!form.profile) next.profile = 'Select the closest profile.';
    if (form.intent.trim().length < 20) next.intent = 'A sentence or two on why you want to attend, please.';
    if (!form.consent) next.consent = 'We need your consent to process the request.';
    return next;
  }
  async function handleSubmit(submitEvent: React.FormEvent<HTMLFormElement>) {
    submitEvent.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    await new Promise(resolve => setTimeout(resolve, 900));
    const registration = submitRegistration({
      eventId: event.id,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      organisation: form.organisation.trim(),
      jobTitle: form.jobTitle.trim(),
      profile: form.profile,
      intent: form.intent.trim(),
      dietary: form.dietary.trim() || undefined
    });
    setForm(emptyForm);
    setStatus('idle');
    onSubmitted(registration);
  }
  const hasErrors = Object.values(errors).some(Boolean);
  return <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-123" data-mp="1fnbxce4fcx3r">
      {hasErrors && status === 'error' && <div role="alert" className="flex items-start gap-3 rounded-lg border border-ochre/30 bg-ochre/5 px-4 py-3 text-sm text-ochre" data-id="element-124" data-mp="1bli2wnktopc1">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-125" data-mp="16orjprdq1dme" />
          <p data-id="element-126" data-mp="11ovg3t8xro7d">Some details need attention before we can send your registration for review.</p>
        </div>}

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-127" data-mp="wur8d7rujc58">
        <div data-id="element-128" data-mp="rwzdq5zthbxc">
          <label className={labelClass} htmlFor="fullName" data-id="element-129" data-mp="rykdca0znl22">
            Full name
          </label>
          <input id="fullName" className={fieldClass} value={form.fullName} onChange={e => update('fullName', e.target.value)} aria-invalid={Boolean(errors.fullName)} aria-describedby={errors.fullName ? 'fullNameError' : undefined} autoComplete="name" data-id="element-130" data-mp="nxgudenn98wm" />
          {errors.fullName && <p id="fullNameError" className="mt-1.5 text-xs text-ochre" data-id="element-131" data-mp="2iyal9frypezt">
              {errors.fullName}
            </p>}
        </div>

        <div data-id="element-132" data-mp="248bptkrskho1">
          <label className={labelClass} htmlFor="email" data-id="element-133" data-mp="299iyrn5o7mwa">
            Work email
          </label>
          <input id="email" type="email" className={fieldClass} value={form.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'emailError' : undefined} autoComplete="email" data-id="element-134" data-mp="1v9rc6y138y6o" />
          {errors.email && <p id="emailError" className="mt-1.5 text-xs text-ochre" data-id="element-135" data-mp="1qchqntgtl6o4">
              {errors.email}
            </p>}
        </div>

        <div data-id="element-136" data-mp="3koxtslebk4or">
          <label className={labelClass} htmlFor="organisation" data-id="element-137" data-mp="3knmnadthoity">
            Organisation
          </label>
          <input id="organisation" className={fieldClass} value={form.organisation} onChange={e => update('organisation', e.target.value)} aria-invalid={Boolean(errors.organisation)} aria-describedby={errors.organisation ? 'organisationError' : undefined} autoComplete="organization" data-id="element-138" data-mp="1924496277x6v" />
          {errors.organisation && <p id="organisationError" className="mt-1.5 text-xs text-ochre" data-id="element-139" data-mp="1rpzcybiyekt4">
              {errors.organisation}
            </p>}
        </div>

        <div data-id="element-140" data-mp="21q9o7qlfhi5n">
          <label className={labelClass} htmlFor="jobTitle" data-id="element-141" data-mp="21oyhpj0llwau">
            Role
          </label>
          <input id="jobTitle" className={fieldClass} value={form.jobTitle} onChange={e => update('jobTitle', e.target.value)} aria-invalid={Boolean(errors.jobTitle)} aria-describedby={errors.jobTitle ? 'jobTitleError' : undefined} autoComplete="organization-title" data-id="element-142" data-mp="1wrygpmilvjql" />
          {errors.jobTitle && <p id="jobTitleError" className="mt-1.5 text-xs text-ochre" data-id="element-143" data-mp="w3o390jb9r4z">
              {errors.jobTitle}
            </p>}
        </div>
      </div>

      <div data-id="element-144" data-mp="160ssxacd84qj">
        <label className={labelClass} htmlFor="profile" data-id="element-145" data-mp="160j4hr6yy5my">
          Which best describes you?
        </label>
        <select id="profile" className={fieldClass} value={form.profile} onChange={e => update('profile', e.target.value)} aria-invalid={Boolean(errors.profile)} aria-describedby={errors.profile ? 'profileError' : undefined} data-id="element-146" data-mp="1b0f7po7udt1g">
          <option value="" data-id="element-147" data-mp="1ortodoea1i5c">Select a profile</option>
          {PROFILE_OPTIONS.map(option => <option key={option} value={option} data-id="element-148" data-mp="1tqwvrfhqc0gh">
              {option}
            </option>)}
        </select>
        {errors.profile && <p id="profileError" className="mt-1.5 text-xs text-ochre" data-id="element-149" data-mp="1ypq2mp2igt7o">
            {errors.profile}
          </p>}
      </div>

      <div data-id="element-150" data-mp="3e8paho6es5z6">
        <label className={labelClass} htmlFor="intent" data-id="element-151" data-mp="3e7wbj9doykrl">
          What brings you to this session?
        </label>
        <textarea id="intent" rows={4} className={fieldClass} value={form.intent} onChange={e => update('intent', e.target.value)} placeholder="A sentence or two on your interest. This is what our team reads when reviewing registrations." aria-invalid={Boolean(errors.intent)} aria-describedby={errors.intent ? 'intentError' : undefined} data-id="element-152" data-mp="39ae0ndg45vgy" />
        {errors.intent && <p id="intentError" className="mt-1.5 text-xs text-ochre" data-id="element-153" data-mp="3n93vsnjdhl2o">
            {errors.intent}
          </p>}
      </div>

      <div data-id="element-154" data-mp="ojdh8ye54xw3">
        <label className={labelClass} htmlFor="dietary" data-id="element-155" data-mp="ojvosr696yjb">
          Dietary or access requirements <span className="font-normal text-ink-muted" data-id="element-156" data-mp="jrcblvbha5f1">(optional)</span>
        </label>
        <input id="dietary" className={fieldClass} value={form.dietary} onChange={e => update('dietary', e.target.value)} data-id="element-157" data-mp="3o9oq4fxpn9jb" />
      </div>

      <div data-id="element-158" data-mp="3f9s5cpefyutj">
        <label className="flex items-start gap-3 text-sm text-ink-soft" data-id="element-159" data-mp="3fabb77qthsr7">
          <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-paper-line text-signal focus:ring-signal/30" checked={form.consent} onChange={e => update('consent', e.target.checked)} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consentError' : undefined} data-id="element-160" data-mp="3k8mm7lsum9b0" />
          <span data-id="element-161" data-mp="1wq6o9yypfb8">
            I consent to Human Planet processing these details to review my registration and issue a ticket, and to
            receiving event emails related to this registration.
          </span>
        </label>
        {errors.consent && <p id="consentError" className="mt-1.5 text-xs text-ochre" data-id="element-162" data-mp="2rkqr2lixgpgc">
            {errors.consent}
          </p>}
      </div>

      <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-signal px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-signal-deep disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto" data-id="element-163" data-mp="2k2j3nrsz6xns">
        {status === 'submitting' ? <>
            <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-164" data-mp="20cyx6o0d21ix" />
            Sending registration
          </> : <>
            <TicketIcon className="h-4 w-4" aria-hidden="true" data-id="element-165" data-mp="2a1z20o1jnr51" />
            Register to attend
          </>}
      </button>

      <p className="text-xs leading-relaxed text-ink-muted" data-id="element-166" data-mp="25aqphj4x3pnn">
        Registrations are reviewed by the Human Planet team, usually within two working days. You will receive
        confirmation of receipt immediately, and your ticket by email once approved.
      </p>
    </form>;
}